import { Col, Form, FormInstance, Radio, Row, Typography } from 'antd';
import { useIntl } from 'react-intl';
import moment, { Moment } from 'moment';
import { ReactNode, useEffect, useState } from 'react';
import {
  CompaniesInfiniteSelectField,
  FloatingFormItem,
  InputDate,
} from '@/components/_common';
import { useWindowSize } from '@/hooks';

export interface ReportDateInterface {
  initialDate: Moment | string;
  finalDate: Moment | string;
  companiesIds?: number[];
}
interface Props {
  form: FormInstance<ReportDateInterface>;
  maxMonthRange?: number;
  dateFilter?: ReactNode;
}

const ReportSelectMonthFields: React.FC<Props> = ({
  form,
  maxMonthRange,
  ...props
}) => {
  const intl = useIntl().formatMessage;
  const { Title } = Typography;
  const { isMobile } = useWindowSize();

  const [monthSelected, setMonthSelected] = useState<string>();
  const startMonth = moment(monthSelected).startOf('month');
  const endMonth = moment(monthSelected).endOf('month');

  const initialDate = Form.useWatch('initialDate', form);
  const finalDate = Form.useWatch('finalDate', form);

  useEffect(() => {
    form.setFieldsValue({
      initialDate: startMonth,
      finalDate: endMonth,
    });
  }, [monthSelected]);

  const months = new Array(6)
    .fill('')
    .map((_, index) => moment().subtract(index + 1, 'months'))
    .reverse();

  function validationDates(value: Moment, isFinalDate: boolean) {
    if (value) {
      const isInvalidDate =
        (initialDate && value.isAfter(finalDate)) ||
        (finalDate && value.isBefore(initialDate));

      const maxMonth =
        !!maxMonthRange &&
        (value.isBefore(
          moment(finalDate).subtract(maxMonthRange, 'months').startOf('day'),
        ) ||
          value.isAfter(
            moment(finalDate).add(maxMonthRange, 'months').endOf('month'),
          )) &&
        !isFinalDate;

      if (isInvalidDate) {
        return Promise.reject(
          new Error(
            `Deve ser ${!isFinalDate ? 'menor' : 'maior'} ou igual a ${
              !isFinalDate
                ? moment(finalDate)?.format('DD/MM/YYYY')
                : moment(initialDate)?.format('DD/MM/YYYY')
            }`,
          ),
        );
      }

      if (maxMonth) {
        return Promise.reject(
          new Error(
            `Deve ser maior ou igual a ${moment(finalDate)
              .subtract(maxMonthRange, 'months')
              .startOf('day')
              ?.format('DD/MM/YYYY')}`,
          ),
        );
      }
    }

    return Promise.resolve();
  }

  return (
    <>
      <Row gutter={[12, 12]}>
        {!!props.dateFilter && <Col span={24}>{props.dateFilter}</Col>}

        <Col span={24}>
          <Title level={5}>
            {intl({ id: 'reports.form.selectTheDesiredMonth' })}
          </Title>

          <Row gutter={16}>
            <Col span={24} md={8}>
              <FloatingFormItem
                label={intl({ id: 'reports.form.in' })}
                name="initialDate"
                initialValue={startMonth}
                rules={[
                  {
                    validator: (_, value) => validationDates(value, false),
                  },
                ]}
              >
                <InputDate
                  disabledDate={(date) =>
                    (!!finalDate && date.isAfter(finalDate)) ||
                    (!!maxMonthRange &&
                      (date.isBefore(
                        moment(finalDate)
                          .subtract(maxMonthRange, 'months')
                          .startOf('day'),
                      ) ||
                        date.isAfter(
                          moment(finalDate)
                            .add(maxMonthRange, 'months')
                            .endOf('month'),
                        )))
                  }
                />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={8}>
              <FloatingFormItem
                label={intl({ id: 'reports.form.from' })}
                name="finalDate"
                initialValue={endMonth}
                rules={[
                  {
                    validator: (_, value) => validationDates(value, true),
                  },
                ]}
              >
                <InputDate
                  disabledDate={(date) =>
                    !!initialDate && date.isBefore(initialDate)
                  }
                />
              </FloatingFormItem>
            </Col>
          </Row>
        </Col>

        <Col span={24}>
          <Row>
            <Title level={5}>
              {intl({ id: 'reports.form.orSelectTheDesiredMonth' })}
            </Title>

            <Col span={24}>
              <Radio.Group
                optionType="button"
                buttonStyle="solid"
                size="large"
                onChange={({ target }) => setMonthSelected(target.value)}
              >
                {months.map((month) => (
                  <Radio
                    key={month.toISOString()}
                    value={month.format('YYYY-MM-DD')}
                    style={{ width: isMobile ? '50%' : '' }}
                  >
                    {month.format('MMM/YYYY').toUpperCase()}
                  </Radio>
                ))}
              </Radio.Group>
            </Col>
          </Row>
        </Col>

        <Col span={24} md={16}>
          <FloatingFormItem
            name="companiesIds"
            label={intl({ id: 'reports.form.companies' })}
          >
            <CompaniesInfiniteSelectField
              allowClear
              mode="multiple"
              queryFilter={{ rows: 50 }}
              queryEnable
              selectAll
              activityFilter
            />
          </FloatingFormItem>
        </Col>
      </Row>
    </>
  );
};

export default ReportSelectMonthFields;
